<template>
  <div class="known-business-entity-contact-invite-code-button">
    <div class="flex items-center justify-between gap-x-2">
      <div>
        <div class="text-sm font-medium">Uitnodigingscode</div>
        <div class="text-2xs leading-3">
          Met deze code kan de dienstverlener een account aanmaken. Kopieer de code of het bericht
          naar het klembord.
        </div>
      </div>
      <div class="flex items-center gap-x-2">
        <GeneralButton
          @click="onClick"
          title="Uitnodigingscode naar klembord kopiëren"
          :color="isCodeCopied ? 'success' : 'primary'"
          :icon="!isCodeCopied ? ['fas', 'clipboard'] : ['fas', 'check']"
        >
          {{ invitationCode }}
        </GeneralButton>
        <GeneralButton
          @click="onCopyMessageClick"
          variant="outlined"
          title="Bericht met uitnodigingscode naar klembord kopiëren"
          :color="isMessageCopied ? 'success' : 'primary'"
          :icon="!isMessageCopied ? ['fas', 'file-lines'] : ['fas', 'check']"
        >
          Bericht
        </GeneralButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue"
import { storeToRefs } from "pinia"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import { useAuthStore } from "@/store/auth.store"

const props = defineProps<{
  invitationCode: string
  recipientName: string
}>()

const isCodeCopied = ref(false)
const isMessageCopied = ref(false)
const authStore = useAuthStore()
const { activeUser } = storeToRefs(authStore)

let codeCopyTimeout: ReturnType<typeof setTimeout>
let messageCopyTimeout: ReturnType<typeof setTimeout>

function pulseCopiedState(state: { value: boolean }, timeoutRef: "code" | "message") {
  state.value = true

  if (timeoutRef === "code") {
    if (codeCopyTimeout) {
      clearTimeout(codeCopyTimeout)
    }
    codeCopyTimeout = setTimeout(() => {
      state.value = false
    }, 1500)
    return
  }

  if (messageCopyTimeout) {
    clearTimeout(messageCopyTimeout)
  }
  messageCopyTimeout = setTimeout(() => {
    state.value = false
  }, 1500)
}

function onClick() {
  copyToClipboard(props.invitationCode)
  pulseCopiedState(isCodeCopied, "code")
}

function onCopyMessageClick() {
  const message = `Beste ${props.recipientName},

Leuk dat je binnenkort bij ons aan de slag gaat! Voor het inzien en bijhouden van je werkzaamheden gebruiken wij de Peanuts app. Hieronder lees je hoe je deze instelt en gebruikt.

1. Download de app via een van de onderstaande links:

Android: https://play.google.com/store/apps/details?id=nl.peanuts.app
iPhone: https://apps.apple.com/nl/app/peanuts-nl/id6740243335

- Open de app en vul de registratiecode: *${props.invitationCode}* in.
- Maak je account aan: Gebruik je telefoonnummer of e-mailadres om je account definitief te maken. Daarna kun je direct je werkoverzicht bekijken.

2. Zo werkt je planning en urenregistratie

- Nieuwe opdrachten: Elke werkdag staan aan het einde van de middag de klussen voor de volgende dag voor je klaar in de app.
- Je werkdag starten: Begin je dag in de app altijd op het tabblad 'Werkdag'. Hier start je jouw dienst; dit zijn de uren die je voor ons als werkgever maakt. Ook je pauzes en algemene opmerkingen voor ons registreer je hier.
- Aan een klus werken: Zodra je dienst is gestart, klik je op een specifieke klus om daar je werkzaamheden te registreren. Dit is de tijd die je daadwerkelijk voor de klant werkt. (Kortom: je 'dienst' is de totale tijd voor je werkgever, de 'klustijd' is de gewerkte tijd per klant).
- Bonnen en bijzonderheden: Maak een duidelijke foto van eventuele bonnen en upload deze direct bij de betreffende klus. Opvallende zaken over de klus kun je noteren in het opmerkingenveld.

3. Administratie en contact
Een kloppende registratie in de app zorgt ervoor dat alle werkzaamheden administratief vlot afgehandeld kunnen worden. Loop je ergens tegenaan of heb je vragen? Bel dan gerust even met de planning.

Alvast veel succes en tot snel!

Met vriendelijke groet,

${activeUser.value?.knownBusinessEntityContact?.person.firstName}`

  copyToClipboard(message)
  pulseCopiedState(isMessageCopied, "message")
}

async function copyToClipboard(message: string) {
  try {
    await navigator.clipboard.writeText(message)
  } catch (error) {
    console.error("Failed to copy to clipboard:", error)
  }
}
</script>

<style scoped></style>
